import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useBlocker } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { RtfEditor } from '../components/RtfEditor.js';
import { rtfToHtml, htmlToRtf } from '../utils/rtf.js';
import { Loader2, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { catalog } from '../api/index.js';
import type { CatalogItem } from '../api/index.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { EnumPickList } from '../components/EnumPickList.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { ContributorCombo } from '../components/ContributorCombo.js';
import { CAST_ROLE_OPTIONS } from '../lib/castRoles.js';
import { ITEM_FORMAT_OPTIONS, ITEM_TYPE_OPTIONS } from '../lib/itemTaxonomy.js';
import { historySlugsFromIglu } from '../lib/iglu-history-slugs.js';
import { useMe } from '../hooks/useAuth.js';

// ─── Sentinel helpers ────────────────────────────────────────────────────────

const SENTINEL = 'ig.field.type.delete';

function unwrapOid(v: unknown): unknown {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    if (typeof o.$oid === 'string') return o.$oid;
    if (typeof o._id === 'string') return o._id;
  }
  return v;
}

function fromIglu(v: unknown, fallback = ''): string {
  const u = unwrapOid(v);
  if (u === SENTINEL || u === null || u === undefined) return fallback;
  return String(u);
}

function fromIgluArr(v: unknown): string {
  if (!Array.isArray(v)) return '';
  const filtered = v
    .map(unwrapOid)
    .filter((x) => x !== SENTINEL && x !== null && x !== undefined);
  return filtered.map(String).join(', ');
}

function igluDateToInput(v: unknown): string {
  const s = fromIglu(v);
  if (!s) return '';
  // "2026-05-12T08:00:00.000" → "2026-05-12T08:00"
  return s.slice(0, 16);
}

function inputToIgluDate(s: string): string | undefined {
  if (!s) return undefined;
  // "2026-05-12T08:00" → "2026-05-12T08:00:00.000"
  return `${s}:00.000`;
}

function splitCSV(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

function igluIdList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map(unwrapOid)
    .filter((x) => x !== SENTINEL && x !== null && x !== undefined)
    .map(String);
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface CastEntry {
  teamMemberId: string;
  roles: string;
}

interface ItemFormValues {
  name: string;
  status: string;
  type: string;
  slug: string;
  isArchived: boolean;
  description: string;
  shortDescription: string;
  webSynopsis: string;
  format: string;
  language: string;
  edition: string;
  bisac: string;
  tags: string;
  keywords: string;
  countryCodes: string;
  isbn13: string;
  bookId: string;
  releaseAt: string;
  originalReleaseAt: string;
  preorderReleaseAt: string;
  showReleaseDateOnPreorder: boolean;
  price: number;
  free: boolean;
  purchasable: boolean;
  featuredOnApp: boolean;
  seriesId: string;
  groupId: string;
  publishers: string[];
  genres: string[];
  imprints: string[];
  sampleUrl: string;
  totalDuration: string;
  cast: CastEntry[];
}

function itemToFormValues(item: CatalogItem): ItemFormValues {
  const castArr = Array.isArray(item.cast)
    ? (item.cast as Array<Record<string, unknown>>)
        .map((c) => ({
          // Iglu may use either 'teamMemberId' or 'teamMember'
          teamMemberId: String(c.teamMemberId ?? c.teamMember ?? ''),
          roles:
            Array.isArray(c.roles) && (c.roles as string[]).length > 0
              ? String((c.roles as string[])[0])
              : String(c.roles ?? c.type ?? ''),
        }))
        .filter((c) => c.teamMemberId && c.teamMemberId !== SENTINEL)
    : [];
  const pubArr = Array.isArray(item.publishers)
    ? (item.publishers as string[]).filter((p) => p !== SENTINEL)
    : [];

  return {
    name: fromIglu(item.name),
    status: fromIglu(item.status, 'draft'),
    type: fromIglu(item.type, 'audioFile'),
    slug: fromIglu(item.slug),
    isArchived: Boolean(item.isArchived),
    description: fromIglu(item.description),
    shortDescription: fromIglu(item.shortDescription),
    webSynopsis: rtfToHtml(fromIglu(item.webSynopsis)),
    format: fromIglu(item.format),
    language: fromIglu(item.language),
    edition: fromIglu(item.edition),
    bisac: fromIgluArr(item.bisac),
    tags: fromIgluArr(item.tags),
    keywords: fromIgluArr(item.keywords),
    countryCodes: fromIgluArr(item.countryCodes),
    isbn13: fromIglu(item.isbn13),
    bookId: fromIglu(item.bookId),
    releaseAt: igluDateToInput(item.releaseAt),
    originalReleaseAt: igluDateToInput(item.originalReleaseAt),
    preorderReleaseAt: igluDateToInput(item.preorderReleaseAt),
    showReleaseDateOnPreorder:
      typeof item.showReleaseDateOnPreorder === 'boolean'
        ? item.showReleaseDateOnPreorder
        : true,
    price: typeof item.price === 'number' ? item.price : 0,
    free: Boolean(item.free),
    purchasable: item.purchasable !== false,
    featuredOnApp: Boolean(item.featuredOnApp),
    seriesId: fromIglu(item.seriesId),
    groupId: fromIglu(item.groupId),
    publishers: pubArr,
    genres: Array.isArray(item.genres)
      ? (item.genres as string[]).filter((g) => g !== SENTINEL)
      : [],
    imprints: igluIdList(item.imprints),
    sampleUrl: fromIglu(item.sampleUrl),
    totalDuration:
      item.totalDuration && item.totalDuration !== SENTINEL
        ? String(item.totalDuration)
        : '',
    cast: castArr,
  };
}

function formToItemPatch(values: ItemFormValues): Record<string, unknown> {
  return {
    name: values.name,
    status: values.status,
    type: values.type,
    slug: values.slug,
    isArchived: values.isArchived,
    description: values.description || undefined,
    shortDescription: values.shortDescription || undefined,
    webSynopsis: htmlToRtf(values.webSynopsis) || undefined,
    format: values.format || undefined,
    language: values.language || undefined,
    edition: values.edition || undefined,
    bisac: splitCSV(values.bisac),
    tags: splitCSV(values.tags),
    keywords: splitCSV(values.keywords),
    countryCodes: splitCSV(values.countryCodes),
    isbn13: values.isbn13 || undefined,
    bookId: values.bookId || undefined,
    releaseAt: inputToIgluDate(values.releaseAt),
    originalReleaseAt: inputToIgluDate(values.originalReleaseAt),
    preorderReleaseAt: inputToIgluDate(values.preorderReleaseAt),
    showReleaseDateOnPreorder: values.showReleaseDateOnPreorder,
    price: values.price,
    free: values.free,
    purchasable: values.purchasable,
    featuredOnApp: values.featuredOnApp,
    seriesId: values.seriesId || SENTINEL,
    groupId: values.groupId || SENTINEL,
    publishers: values.publishers,
    genres: values.genres,
    imprints: values.imprints,
    sampleUrl: values.sampleUrl || undefined,
    totalDuration: values.totalDuration ? Number(values.totalDuration) : undefined,
    cast: values.cast
      .filter((c) => c.teamMemberId)
      .map((c) => ({
        teamMemberId: c.teamMemberId,
        roles: c.roles.split(',').map((r) => r.trim()).filter(Boolean),
      })),
  };
}

// ─── Small UI helpers ────────────────────────────────────────────────────────

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

const INPUT_CLS =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50';
const TEXTAREA_CLS =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 min-h-[80px] resize-y';

const ITEM_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-6 rounded-full transition-colors ${
          checked ? 'bg-brand-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
      </div>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

// ─── Save bar ────────────────────────────────────────────────────────────────

function SaveBar({
  isDirty,
  saveStatus,
  saveError,
}: {
  isDirty: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  saveError: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="flex items-center gap-3">
        {isDirty && saveStatus === 'idle' && (
          <span className="text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
            Unsaved changes
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
            Saved ✓
          </span>
        )}
        {saveStatus === 'error' && (
          <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1">
            Error: {saveError}
          </span>
        )}
      </div>
      <Button type="submit" loading={saveStatus === 'saving'} disabled={!isDirty && saveStatus === 'idle'}>
        Save changes
      </Button>
    </div>
  );
}

// ─── High-risk confirmation banner ───────────────────────────────────────────

function HighRiskConfirm({
  fields,
  onConfirm,
  onCancel,
}: {
  fields: string[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="text-amber-500 mt-0.5 flex-shrink-0" size={20} />
          <div>
            <p className="font-semibold text-gray-900">High-risk field change</p>
            <p className="text-sm text-gray-600 mt-1">
              You are changing{' '}
              <strong>{fields.join(', ')}</strong>. This may affect existing purchases, links, or
              integrations. Are you sure?
            </p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            Yes, save anyway
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ItemEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me, isLoading: meLoading } = useMe();

  useEffect(() => {
    if (meLoading) return;
    if (me && me.enableEdit !== true && id) {
      navigate(`/catalog/${id}`, { replace: true });
    }
  }, [me, meLoading, id, navigate]);

  const { data: item, isLoading, error } = useQuery({
    queryKey: ['catalog-item', id],
    queryFn: () => catalog.item(id!),
    enabled: !!id,
  });

  const { data: seriesData } = useQuery({
    queryKey: ['catalog-series'],
    queryFn: catalog.series,
  });
  const { data: publishersData } = useQuery({
    queryKey: ['catalog-publishers'],
    queryFn: catalog.publishers,
  });
  const { data: imprintsData } = useQuery({
    queryKey: ['catalog-imprints'],
    queryFn: catalog.imprints,
    staleTime: 10 * 60 * 1000,
  });
  const { data: genresData } = useQuery({
    queryKey: ['catalog-genres'],
    queryFn: catalog.genres,
    staleTime: 10 * 60 * 1000,
  });

  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    control,
    reset,
  } = useForm<ItemFormValues>({
    defaultValues: {
      name: '',
      status: 'draft',
      type: 'audioFile',
      slug: '',
      isArchived: false,
      description: '',
      shortDescription: '',
      webSynopsis: '',
      format: '',
      language: '',
      edition: '',
      bisac: '',
      tags: '',
      keywords: '',
      countryCodes: '',
      isbn13: '',
      bookId: '',
      releaseAt: '',
      originalReleaseAt: '',
      preorderReleaseAt: '',
      showReleaseDateOnPreorder: true,
      price: 0,
      free: false,
      purchasable: true,
      featuredOnApp: false,
      seriesId: '',
      groupId: '',
      publishers: [],
      genres: [],
      imprints: [],
      sampleUrl: '',
      totalDuration: '',
      cast: [],
    },
  });

  const { fields: castFields, append: appendCast, remove: removeCast } = useFieldArray({
    control,
    name: 'cast',
  });

  // Populate form when item loads
  useEffect(() => {
    if (item) reset(itemToFormValues(item));
  }, [item, reset]);

  const selectedSeriesId = watch('seriesId');
  const selectedPublishers = watch('publishers');
  const selectedGenres = watch('genres');
  const selectedImprints = watch('imprints');

  const { data: groupsData } = useQuery({
    queryKey: ['catalog-groups', selectedSeriesId],
    queryFn: () => catalog.groups(selectedSeriesId),
    enabled: !!selectedSeriesId,
  });

  // Save state
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');

  // High-risk confirmation
  const [pendingValues, setPendingValues] = useState<ItemFormValues | null>(null);
  const [highRiskFields, setHighRiskFields] = useState<string[]>([]);
  const [genreFilter, setGenreFilter] = useState('');

  async function doSave(values: ItemFormValues) {
    setSaveStatus('saving');
    setSaveError('');
    try {
      await catalog.updateItem(id!, formToItemPatch(values));
      await queryClient.invalidateQueries({ queryKey: ['catalog-item', id] });
      reset(values);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: unknown) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Unknown error');
    }
  }

  function onSubmit(values: ItemFormValues) {
    if (!item) return;

    // Detect high-risk field changes
    const originalName = fromIglu(item.name);
    const originalSlug = fromIglu(item.slug);
    const originalIsbn = fromIglu(item.isbn13);
    const originalBookId = fromIglu(item.bookId);
    const risks: string[] = [];
    if (originalName && values.name !== originalName) risks.push('name');
    if (originalSlug && values.slug !== originalSlug) risks.push('slug');
    if (originalIsbn && values.isbn13 !== originalIsbn) risks.push('isbn13');
    if (originalBookId && values.bookId !== originalBookId) risks.push('bookId');

    if (risks.length > 0) {
      setPendingValues(values);
      setHighRiskFields(risks);
      return;
    }

    void doSave(values);
  }

  function handleConfirmHighRisk() {
    if (pendingValues) void doSave(pendingValues);
    setPendingValues(null);
    setHighRiskFields([]);
  }

  function handleCancelHighRisk() {
    setPendingValues(null);
    setHighRiskFields([]);
  }

  function togglePublisher(pubId: string) {
    const current = selectedPublishers ?? [];
    const next = current.includes(pubId)
      ? current.filter((p) => p !== pubId)
      : [...current, pubId];
    setValue('publishers', next, { shouldDirty: true });
  }

  function toggleGenre(genreId: string) {
    const current = selectedGenres ?? [];
    const next = current.includes(genreId)
      ? current.filter((g) => g !== genreId)
      : [...current, genreId];
    setValue('genres', next, { shouldDirty: true });
  }

  function toggleImprint(imprintId: string) {
    const current = selectedImprints ?? [];
    const next = current.includes(imprintId)
      ? current.filter((x) => x !== imprintId)
      : [...current, imprintId];
    setValue('imprints', next, { shouldDirty: true });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !item) {
    return <div className="p-8 text-red-600">Item not found</div>;
  }

  const seriesList = seriesData?.series ?? [];
  const groupsList = groupsData?.groups ?? [];
  const genresList = genresData?.genres ?? [];
  const publishersList = publishersData?.publishers ?? [];
  const imprintsList = imprintsData?.imprints ?? [];

  const genresFiltered = useMemo(() => {
    const q = genreFilter.trim().toLowerCase();
    if (!q) return genresList;
    return genresList.filter((g) => g.name.toLowerCase().includes(q));
  }, [genresList, genreFilter]);

  const slugHistory = historySlugsFromIglu(item?.historySlugs);

  // Warn before navigating away with unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      formState.isDirty &&
      saveStatus !== 'saved' &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const seriesSelectOptions = useMemo(
    () => [{ value: '', label: '— None —' }, ...seriesList.map((s) => ({ value: s._id, label: s.name }))],
    [seriesList],
  );
  const groupSelectOptions = useMemo(
    () => [{ value: '', label: '— None —' }, ...groupsList.map((g) => ({ value: g._id, label: g.name }))],
    [groupsList],
  );

  return (
    <>
      {blocker.state === 'blocked' && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Discard unsaved changes?</h2>
            <p className="text-sm text-gray-500">You have unsaved changes. If you leave now, they will be lost.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => blocker.reset?.()}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Stay
              </button>
              <button
                onClick={() => blocker.proceed?.()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Discard & leave
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingValues && highRiskFields.length > 0 && (
        <HighRiskConfirm
          fields={highRiskFields}
          onConfirm={handleConfirmHighRisk}
          onCancel={handleCancelHighRisk}
        />
      )}

      <div className="p-8 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <Link
            to={`/catalog/${id}`}
            className="text-sm text-brand-600 hover:underline mb-2 inline-block"
          >
            ← Back to item
          </Link>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Edit: {fromIglu(item.name, '(no title)')}
              </h1>
              <p className="text-sm text-gray-500 mt-1 font-mono">{item._id}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/catalog/${id}`)}>
              Cancel
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <SaveBar isDirty={formState.isDirty} saveStatus={saveStatus} saveError={saveError} />

          {/* ── Core ── */}
          <FormSection title="Core">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required>
                <input
                  {...register('name', { required: true })}
                  className={INPUT_CLS}
                  placeholder="Title"
                />
              </Field>
              <Field
                label="Slug"
                hint="Warning: changing slug may break existing links. Iglu keeps prior slugs here for redirects."
              >
                <input {...register('slug')} className={INPUT_CLS} placeholder="my-audiobook" />
                {slugHistory.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                    <span className="text-xs text-gray-500">Previous slugs:</span>
                    {slugHistory.map((s) => (
                      <span
                        key={s}
                        className="font-mono text-[11px] bg-gray-100 border border-gray-200 rounded px-2 py-0.5 text-gray-700"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </Field>
              <Field label="Status">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v);
                        field.onBlur();
                      }}
                      options={ITEM_STATUS_OPTIONS}
                      placeholder="Status"
                    />
                  )}
                />
              </Field>
              <Field label="Type">
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v);
                        field.onBlur();
                      }}
                      options={ITEM_TYPE_OPTIONS}
                      placeholder="Type"
                    />
                  )}
                />
              </Field>
            </div>
            <div className="flex gap-6 flex-wrap">
              <Toggle
                label="Archived"
                checked={watch('isArchived')}
                onChange={(v) => setValue('isArchived', v, { shouldDirty: true })}
              />
            </div>
          </FormSection>

          {/* ── Description ── */}
          <FormSection title="Description">
            <Field label="Description">
              <textarea {...register('description')} className={TEXTAREA_CLS} rows={4} />
            </Field>
            <Field label="Short Description">
              <textarea {...register('shortDescription')} className={TEXTAREA_CLS} rows={2} />
            </Field>
            <Field label="Web Synopsis">
              <Controller
                name="webSynopsis"
                control={control}
                render={({ field }) => (
                  <RtfEditor value={field.value} onChange={field.onChange} />
                )}
              />
            </Field>
          </FormSection>

          {/* ── Classification ── */}
          <FormSection title="Classification">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Format" hint="Product presentation — not edition (see Edition below).">
                <Controller
                  name="format"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v);
                        field.onBlur();
                      }}
                      options={ITEM_FORMAT_OPTIONS}
                      placeholder="Format"
                      allowClear={false}
                    />
                  )}
                />
              </Field>
              <Field label="Language">
                <input {...register('language')} className={INPUT_CLS} placeholder="en" />
              </Field>
              <Field label="Edition">
                <input {...register('edition')} className={INPUT_CLS} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="BISAC" hint="Comma-separated codes">
                <input {...register('bisac')} className={INPUT_CLS} placeholder="FIC000000, FIC031000" />
              </Field>
              <Field label="Genres">
                {genresList.length > 0 ? (
                  <div>
                    <input
                      type="text"
                      value={genreFilter}
                      onChange={(e) => setGenreFilter(e.target.value)}
                      placeholder="Filter genres…"
                      className={`${INPUT_CLS} mb-2`}
                    />
                    <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                      {genresFiltered.map((g) => (
                        <label key={g._id} className="flex items-center gap-2 cursor-pointer py-0.5">
                          <input
                            type="checkbox"
                            checked={selectedGenres.includes(g._id)}
                            onChange={() => toggleGenre(g._id)}
                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                          <span className="text-sm text-gray-700">{g.name}</span>
                        </label>
                      ))}
                      {genresFiltered.length === 0 && (
                        <p className="text-sm text-gray-400 py-2 text-center">No genres match</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Loading genres…</p>
                )}
              </Field>
              <Field label="Tags" hint="Comma-separated">
                <input {...register('tags')} className={INPUT_CLS} />
              </Field>
              <Field label="Keywords" hint="Comma-separated">
                <input {...register('keywords')} className={INPUT_CLS} />
              </Field>
              <Field label="Country Codes" hint="Comma-separated ISO codes">
                <input {...register('countryCodes')} className={INPUT_CLS} placeholder="US, CA, GB" />
              </Field>
            </div>
          </FormSection>

          {/* ── Identifiers ── */}
          <FormSection title="Identifiers">
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="ISBN-13"
                hint={fromIglu(item.isbn13) ? 'Warning: changing ISBN may affect metadata' : ''}
              >
                <input {...register('isbn13')} className={INPUT_CLS} placeholder="9780000000000" />
              </Field>
              <Field label="Book ID">
                <input {...register('bookId')} className={INPUT_CLS} />
              </Field>
            </div>
          </FormSection>

          {/* ── Dates ── */}
          <FormSection title="Dates">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Release Date">
                <input type="datetime-local" {...register('releaseAt')} className={INPUT_CLS} />
              </Field>
              <Field label="Original Release">
                <input
                  type="datetime-local"
                  {...register('originalReleaseAt')}
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="Preorder Release">
                <input
                  type="datetime-local"
                  {...register('preorderReleaseAt')}
                  className={INPUT_CLS}
                />
              </Field>
            </div>
            <Toggle
              label="Show release date on preorder"
              checked={watch('showReleaseDateOnPreorder')}
              onChange={(v) => setValue('showReleaseDateOnPreorder', v, { shouldDirty: true })}
            />
          </FormSection>

          {/* ── Pricing ── */}
          <FormSection title="Pricing">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price ($)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('price', { valueAsNumber: true })}
                  className={INPUT_CLS}
                />
              </Field>
            </div>
            <div className="flex gap-6 flex-wrap">
              <Toggle
                label="Free"
                checked={watch('free')}
                onChange={(v) => setValue('free', v, { shouldDirty: true })}
              />
              <Toggle
                label="Purchasable"
                checked={watch('purchasable')}
                onChange={(v) => setValue('purchasable', v, { shouldDirty: true })}
              />
              <Toggle
                label="Featured on app"
                checked={watch('featuredOnApp')}
                onChange={(v) => setValue('featuredOnApp', v, { shouldDirty: true })}
              />
            </div>
          </FormSection>

          {/* ── Series & Group ── */}
          <FormSection title="Series & Group">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Series">
                <Controller
                  name="seriesId"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v);
                        setValue('groupId', '', { shouldDirty: true });
                        field.onBlur();
                      }}
                      options={seriesSelectOptions}
                      placeholder="Series"
                    />
                  )}
                />
              </Field>
              <Field label="Group">
                <Controller
                  name="groupId"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v);
                        field.onBlur();
                      }}
                      options={groupSelectOptions}
                      placeholder="Group"
                      disabled={!selectedSeriesId}
                    />
                  )}
                />
              </Field>
            </div>
          </FormSection>

          {/* ── Publishers & Imprints ── */}
          <FormSection title="Publishers & Imprints">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Publishers</p>
                {publishersList.length > 0 ? (
                  <EnumPickList
                    items={publishersList}
                    selectedIds={selectedPublishers}
                    onToggle={togglePublisher}
                    filterPlaceholder="Filter publishers…"
                    emptyHint="No publishers match"
                  />
                ) : (
                  <p className="text-sm text-gray-400">Loading publishers…</p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Imprints</p>
                {imprintsList.length > 0 ? (
                  <EnumPickList
                    items={imprintsList}
                    selectedIds={selectedImprints}
                    onToggle={toggleImprint}
                    filterPlaceholder="Filter imprints…"
                    emptyHint="No imprints match"
                  />
                ) : (
                  <p className="text-sm text-gray-400">Loading imprints…</p>
                )}
              </div>
            </div>
          </FormSection>

          {/* ── Media ── */}
          <FormSection title="Media">
            <Field label="Sample URL">
              <input
                {...register('sampleUrl')}
                className={INPUT_CLS}
                placeholder="https://…"
                type="url"
              />
            </Field>
            <Field label="Total Duration (seconds)">
              <input
                type="number"
                min="0"
                {...register('totalDuration')}
                className={INPUT_CLS}
                placeholder="0"
              />
            </Field>
          </FormSection>

          {/* ── Cast ── */}
          <FormSection title="Cast">
            {castFields.length === 0 && (
              <p className="text-sm text-gray-400">No contributors assigned.</p>
            )}
            <div className="space-y-2">
              {castFields.map((field, idx) => (
                <div key={field.id} className="flex gap-2 items-start flex-wrap">
                  <Controller
                    name={`cast.${idx}.teamMemberId`}
                    control={control}
                    render={({ field }) => (
                      <ContributorCombo
                        value={field.value}
                        onChange={(v) => {
                          field.onChange(v);
                          field.onBlur();
                        }}
                      />
                    )}
                  />
                  <Controller
                    name={`cast.${idx}.roles`}
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value}
                        onChange={(v) => {
                          field.onChange(v);
                          field.onBlur();
                        }}
                        options={CAST_ROLE_OPTIONS}
                        placeholder="Role"
                        emptyLabel="— Role —"
                        className="w-48 shrink-0"
                      />
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => removeCast(idx)}
                    className="text-red-400 hover:text-red-600 p-1 flex-shrink-0 mt-2"
                    title="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => appendCast({ teamMemberId: '', roles: '' })}
            >
              <Plus size={14} /> Add contributor
            </Button>
          </FormSection>

          <SaveBar isDirty={formState.isDirty} saveStatus={saveStatus} saveError={saveError} />
        </form>
      </div>
    </>
  );
}
